<!--
  File name: Users.vue
  Program Description:
    - This is the list of all users in the system, divided into three different
      tabs as follows:
        * All users
        * Internal users
        * External users
    - This also include the functions for create, update, delete, read, disable and
      enable users
    - This file also fetches the user list from the server
-->
<template>
  <div class="page">
    <v-container
      flex
      class="container"
    >
      <v-row justify="center">
        <v-col>
          <v-card
            tile
            flat
            color="transparent"
          >
            <v-toolbar
              color="transparent"
              flat
            >
              <v-toolbar-title class="text-h4 font-weight-bold px-0">
                USERS
              </v-toolbar-title>
              <v-spacer />
              <v-btn
                v-if="showLabel('canCreateByUserRole')"
                id="add-user-button"
                style="background-color:#162b54;  "
                color="white"
                text
                type="button"
                :to="{ path: `${$route.params.userRole}/create` }"
              >
                <v-icon class="mr-2">
                  mdi-plus
                </v-icon> {{ getLabel('canCreateByUserRole', 'Add User') }}
              </v-btn>
            </v-toolbar>
          </v-card>
        </v-col>
      </v-row>
      <v-row class="flex-grow-1">
        <v-col>
          <v-tabs
            v-model="tab"
            grow
          >
            <v-tab
              v-for="item in items"
              :id="`${item.tab}-tab-button`"
              :key="item.tab"
              @click="refreshTable(item.tab)"
            >
              {{ item.tab }}
            </v-tab>
          </v-tabs>
          <v-tabs-items
            v-model="tab"
            class="shadow radius rounded-lg"
          >
            <v-tab-item
              v-for="item in items"
              :key="item.tab"
              :transition="false"
            />
            <v-card
              tile
            >
              <v-card-text>
                <v-data-table
                  item-key="_id"
                  :headers="headers"
                  :items="isRegisteredPic ? filteredRegisteredPic : users"
                  :options.sync="tableOptions"
                  :search="isRegisteredPic ? searchWord : undefined"
                  :server-items-length="isRegisteredPic ? -1 : totalUsers"
                  :loading="isRegisteredPic ? registeredPicIsLoading : $data.$meta.isLoading"
                  :footer-props="$data.$meta.footerProps"
                >
                  <template
                    v-if="showLabel('canReadUsers')"
                    v-slot:top
                  >
                    <v-row
                      v-if="isRegisteredPic"
                      class="mb-n10"
                    >
                      <v-col
                        class="py-0 my-0 d-flex align-center"
                        cols="6"
                      >
                        <p>
                          No. of Registered PIC:
                          <v-chip class="primary ml-3">
                            {{ registeredPic.length }}
                          </v-chip>
                        </p>
                      </v-col>
                      <v-col cols="6">
                        <p>
                          No. of Unregistered PIC:
                          <v-chip class="primary ml-3">
                            {{ unregisteredPicLength }}
                          </v-chip>
                        </p>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <v-menu
                          v-model="filter.menu"
                          :close-on-content-click="false"
                          max-width="600"
                          min-width="300"
                          offset-y
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              id="filter-btn"
                              text
                              v-bind="attrs"
                              v-on="on"
                            >
                              <v-icon large>
                                mdi-filter-variant
                              </v-icon>
                            </v-btn>
                          </template>
                          <v-card
                            class="pa-3"
                            :loading="filter.loading"
                          >
                            <v-card-title
                              class="px-0 text-h6 font-weight-medium"
                              style="color:#162b54;"
                            >
                              Filters
                              <v-spacer />
                              <v-btn
                                icon
                                @click="resetFilter(), isRegisteredPic && refreshTable(selectedTab)"
                              >
                                <v-icon>
                                  mdi-refresh
                                </v-icon>
                              </v-btn>
                            </v-card-title>
                            <v-combobox
                              v-model="filter.header"
                              outlined
                              dense
                              label="Filter"
                              :items="headerVal"
                            />
                            <v-combobox
                              v-model="filter.boxVal"
                              :items="filter.checkbox"
                              :label="`Search ${filter.header}`"
                              :disabled="filter.checkbox.length === 0"
                              :dense="filter.boxVal.length === 0"
                              outlined
                              chips
                              multiple
                              deletable-chips
                            />
                          </v-card>
                        </v-menu>
                      </v-col>
                      <v-col>
                        <v-text-field
                          v-model.trim="searchWord"
                          :label="getLabel('canReadUsers', 'Search')"
                          single-line
                          dense
                          outlined
                          hide-details
                          append-icon="search"
                          :loading="isRegisteredPic ? registeredPicIsLoading : $data.$meta.isLoading"
                          :disabled="isRegisteredPic ? registeredPicIsLoading : $data.$meta.isLoading"
                          @keyup.enter="!isRegisteredPic && (enteredSearchWord = true, refreshTable(selectedTab, search = true), tableOptions.page = 1)"
                          @keyup.delete="!isRegisteredPic && clearSearch(selectedTab)"
                          @click:append="!isRegisteredPic && refreshTable(selectedTab)"
                        />
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col class="pt-0">
                        <v-divider />
                      </v-col>
                    </v-row>
                  </template>
                  <template
                    v-slot:[`item.status`]="{ item }"
                  >
                    <div class="status-switch">
                      <v-switch
                        v-model="item.isActive"
                        color="success"
                        @click="reasonDialog(item)"
                      />
                    </div>
                  </template>
                  <template v-slot:[`item.actions`]="{ item }">
                    <v-spacer />
                    <router-link
                      v-if="showLabel('canReadUserByUserRole') && checkRoleCount(item.userRole) === 1"
                      :to="{ path: `${item.userRole}/view/${item._id}` }"
                    >
                      <v-btn
                        :id="`view-profile-button`"
                        icon
                        :color="getIconColor({ role: 'canReadUserByUserRole' })"
                      >
                        <v-tooltip left>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                            >
                              mdi-eye
                            </v-icon>
                          </template>
                          <span>View</span>
                        </v-tooltip>
                      </v-btn>
                    </router-link>
                    <router-link
                      v-else-if="showLabel('canReadUserByUserRole') && checkRoleCount(item.userRole) > 1"
                      :to="{ path: `profile/view/${item._id}` }"
                    >
                      <v-btn
                        :id="`view-profile-button`"
                        icon
                        :color="getIconColor({ role: 'canReadUserByUserRole' })"
                      >
                        <v-tooltip left>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                            >
                              mdi-eye
                            </v-icon>
                          </template>
                          <span>View</span>
                        </v-tooltip>
                      </v-btn>
                    </router-link>
                    <router-link
                      v-if="showLabel('canUpdateUserByUserRole') && checkRoleCount(item.userRole) === 1 && item._status === 'active'"
                      :to="{ path: `${item.userRole}/edit/${item._id}` }"
                    >
                      <v-btn
                        :id="`edit-profile-button`"
                        icon
                        :color="getIconColor({ role: 'canUpdateUserByUserRole' })"
                      >
                        <v-tooltip left>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                            >
                              mdi-pencil-box-outline
                            </v-icon>
                          </template>
                          <span>Edit</span>
                        </v-tooltip>
                      </v-btn>
                    </router-link>
                    <router-link
                      v-else-if="showLabel('canUpdateUserByUserRole') && checkRoleCount(item.userRole) > 1 && item._status === 'active'"
                      :to="{ path: `profile/edit/${item._id}` }"
                    >
                      <v-btn
                        :id="`edit-table-button`"
                        icon
                        :color="getIconColor({ role: 'canUpdateUserByUserRole' })"
                      >
                        <v-tooltip left>
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              v-on="on"
                            >
                              mdi-pencil-box-outline
                            </v-icon>
                          </template>
                          <span>Edit</span>
                        </v-tooltip>
                      </v-btn>
                    </router-link>
                    <v-btn
                      :id="`delete-table-button`"
                      icon
                      :color="getIconColor({ role: 'canUpdateUserByUserRole' })"
                      @click="deleteDialog(item)"
                    >
                      <v-tooltip left>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            v-bind="attrs"
                            v-on="on"
                          >
                            mdi-delete
                          </v-icon>
                        </template>
                        <span>Delete User</span>
                      </v-tooltip>
                    </v-btn>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-tabs-items>
        </v-col>
      </v-row>
      <v-dialog
        v-model="reasonModal.modal"
        width="35%"
        persistent
      >
        <v-card class="pa-4">
          <v-card-title
            class="pb-8"
            color="#162B54"
          >
            <b>{{ reasonModal.header }}</b>
          </v-card-title>
          <v-textarea
            id="reasonTextArea"
            v-model="reason"
            outlined
            class="pa-5"
            name="input-7-4"
            :label="reasonModal.label"
            :error-messages="genericError('reason', 'Reason')"
            @input="$v.reason.$touch()"
            @blur="$v.reason.$touch()"
          />
          <v-card-actions>
            <v-spacer />
            <template>
              <v-btn
                id="cancel-reason-button"
                color="primary"
                text
                @click="reasonModal.cancel"
              >
                Cancel
              </v-btn>
              <v-btn
                id="continue-button"
                class="pl-8 pr-8"
                color="primary"
                @click="reasonModal.continue"
              >
                Continue
              </v-btn>
            </template>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import users from '@/utils/webapi/users'
import tables from '@/meta/tables'
import internalTable from '@/meta/userInternalTable'
import externalTable from '@/meta/userExternalTable'
import { privilegeMixin } from 'session-plugin'
import { capitalize, encodeReqQuery, getFullName } from '@/utils/helpers'
// import ActionModal from '@/components/local/ActionModal'
import { validationMixin, validationErrors, custom } from '@/utils/validations'
import { mapGetters } from 'vuex'
import crud from '@/utils/rest/crud'

export default {
  name: 'Users',
  components: {
    // ActionModal
  },
  mixins: [privilegeMixin, validationMixin],
  data: () => ({
    selectedTab: 'All',
    selectedLines: [],
    searchWord: '',
    enteredSearchWord: false,
    tempSearchWord: '',
    totalUsers: 0,
    users: [],
    reason: '',
    name: '',
    reasonModal: {
      label: '',
      header: '',
      modal: false,
      cancel: '',
      continue: ''
    },
    filter: {
      menu: false,
      header: '',
      checkbox: [],
      boxVal: [],
      loading: false,
      init: false,
      count: 0
    },
    updateType: '',
    tableOptions: {},
    $meta: {
      isLoading: false,
      footerProps: {
        'items-per-page-options': [5, 10, 20, 40]
      },
      reasonDialog: false,
      dialog0: false
    },
    tab: null,
    items: [
      { tab: 'All' },
      { tab: 'Internal' },
      { tab: 'External' },
      { tab: 'Registered PIC' }
    ],
    registeredPic: [],
    filteredRegisteredPic: [],
    unregisteredPicLength: 0,
    registeredPicIsLoading: true,
    menu: false
  }),
  computed: {
    ...validationErrors,
    ...mapGetters('lookups', {
      profile: 'getCurrentUser'
    }),
    headers () {
      switch (this.selectedTab) {
        case 'All':
          return tables[this.userRole].columns
        case 'Internal':
          return internalTable[this.userRole].columns
        case 'External':
          return externalTable[this.userRole].columns
        default:
          return tables[this.userRole].columns
      }
    },
    headerVal () {
      const value = this.headers.map(item => item.text)
      value.pop()
      return value
    },
    userRole () {
      return this.$route.params.userRole
    },
    properties () {
      return {
        label: 'Reason',
        name: 'reason',
        value: 'registrar',
        textFieldClass: 'required-field',
        'error-messages': this.genericError('reason', 'Reason'),
        outlined: true
      }
    },
    fullName () {
      return getFullName(this.selectedLines[0])
    },
    startAt () {
      return (this.tableOptions.page - 1) * this.tableOptions.itemsPerPage
    },
    isRegisteredPic () {
      return this.selectedTab === 'Registered PIC'
    }
  },
  watch: {
    selectedTab () {
      this.tableOptions.page = 1
      this.filter.init = false
      this.resetFilter()
      this.searchWord = ''
    },
    '$route.params.userRole' () {
      this.refreshTable(this.selectedTab)
    },
    tableOptions: {
      handler () {
        return !this.isRegisteredPic && this.refreshTable(this.selectedTab, this.enteredSearchWord)
      },
      deep: true
    },
    '$data.$meta.reasonDialog' (val) {
      !val && (this.selectedLines = [])
    },
    'filter.header': function (val) {
      if (!this.filter.header) return
      this.filter.boxVal = []
      if (this.isRegisteredPic) this.setPicFilterItems(val)
      else this.filterItems('items')
    },
    'filter.boxVal': async function (val) {
      if (this.isRegisteredPic) this.filterRegisteredPic(val)
      else {
        await this.onFilter(val)
      }
      this.filter.dates = []
      this.filterItems('items')
    },
    'filter.dates': async function (value) {
      await this.onFilter(value)
    }
  },
  created () {
    this.setRegisteredPic()
  },
  methods: {
    capitalize,
    // returns string 'profile' if userRole is more than 1, else, returns value of userRole
    role (userRole) {
      return userRole.length > 1 ? 'profile' : userRole
    },
    // returns userRole count
    checkRoleCount (userRole) {
      return userRole.split(', ').length
    },
    // resets search field and table rows
    clearSearch (tab) {
      if (!this.searchWord.length) {
        this.enteredSearchWord = false
        this.searchWord = ''
        this.refreshTable(tab)
        this.tableOptions.page = 1
      }
    },
    // fetches table contents depending on tab and search props
    async refreshTable (tab, search = false) {
      if (this.filter.boxVal.length > 0) return this.filterItems('filter', tab)
      const data = await this.getDataFromApi(tab, search)
      for (const item of data.items) {
        item.isActive = item._status === 'active'
      }
      this.users = data.items
      this.totalUsers = data.total
      if (search) {
        this.selectedLines = []
      }
      this.$data.$meta.reasonDialog = false
    },
    // fetch users for tabs 'All', 'Internal', or 'External'
    getDataFromApi: async function (tab, search = false) {
      this.selectedTab = tab
      this.$data.$meta.isLoading = true
      let items = []
      let total = 0
      let advancedQuery
      const order = (this.tableOptions.sortDesc.length > 0 && this.tableOptions.sortDesc[0] === true) ? -1 : 1
      const { sortBy: [sort] } = this.tableOptions
      const userEmail = this.profile.email || (await this.$store.dispatch('lookups/getUser')).email
      if (this.selectedTab === 'All') {
        advancedQuery = encodeURI(JSON.stringify({ _status: { $ne: 'deleted' }, email: { $ne: userEmail } }))
      } else {
        advancedQuery = encodeURI(JSON.stringify({ _status: { $ne: 'deleted' }, email: { $ne: userEmail }, userType: { $eq: tab } }))
      }
      const query = encodeReqQuery({
        start: this.startAt,
        count: this.tableOptions.itemsPerPage,
        search: search ? this.searchWord : '',
        sortBy: sort === 'dateCreated' ? '_id' : sort,
        asc: order,
        dataview: this.$s.userRole && this.$s.userRole.includes('admin') ? 'default' : 'table',
        advancedQuery: advancedQuery
      })
      const resp = await users.allUsers(query)
      const data = await resp.json()
      if (resp.ok) {
        if (!this.filter.init) this.filter.count = data.total
        items = data.entries
        total = data.total
        this.filter.init = true
      }
      this.$data.$meta.isLoading = false
      return {
        items,
        total
      }
    },
    // item filtering
    async filterItems (action, tab) {
      if (this.users.length === 0) return
      if (tab) this.selectedTab = tab
      const header = this.filter.header
      let toSearch
      let advancedQuery
      let filtered
      let list
      const rolesArray = []
      const userEmail = this.profile.email || (await this.$store.dispatch('lookups/getUser')).email
      if (this.selectedTab === 'All') {
        advancedQuery = encodeURI(JSON.stringify({ _status: { $ne: 'deleted' }, email: { $ne: userEmail } }))
      } else {
        advancedQuery = encodeURI(JSON.stringify({ _status: { $ne: 'deleted' }, email: { $ne: userEmail }, userType: { $eq: this.selectedTab } }))
      }
      const query = encodeReqQuery({
        advancedQuery: advancedQuery,
        count: this.filter.count
      })

      const resp = await users.allUsers(query)
      const data = await resp.json()
      header === 'Name' ? toSearch = 'fullName' : header === 'Official DPO Email' || header === 'Official Email' ? toSearch = 'email' : header === 'Contact No.' ? toSearch = 'contactNumber' : header === 'Sex' ? toSearch = 'sex' : header === 'Role' ? toSearch = 'userRole' : header === 'Status' ? toSearch = '_status' : header === 'Division' ? toSearch = 'division' : header === 'Designation' ? toSearch = 'designation' : header === 'Organization' ? toSearch = 'organization' : header === 'Date Created' ? toSearch = 'dateCreated' : toSearch = ''

      if (action === 'items') {
        filtered = data.entries.map((i) => {
          const item = i[`${toSearch}`]
          if (toSearch === 'dateCreated') return item.split(' ')[0]
          if (toSearch !== 'userRole') return item
          return item.split(', ')
        })
        if (toSearch === 'userRole') {
          filtered.forEach((i) => {
            i.forEach((x) => {
              if (!rolesArray.includes(x)) {
                rolesArray.push(x)
              }
            })
          })
        }
        list = toSearch !== 'userRole' ? [...new Set(filtered)] : [...new Set(rolesArray)]
        this.filter.checkbox = list.filter(Boolean)
      } else {
        filtered = data.entries.filter((i) => {
          i.isActive = i._status === 'active'
          const dateCreated = new Date(i.dateCreated)
          if (toSearch === 'dateCreated') return this.filter.boxVal.includes(`${dateCreated.getMonth() + 1}/${dateCreated.getDate()}/${dateCreated.getFullYear()}`)
          if (toSearch !== 'userRole') return this.filter.boxVal.includes(i[`${toSearch}`])
          const availableRoles = i[`${toSearch}`].split(', ')

          for (const x of this.filter.boxVal) {
            const val = availableRoles.includes(x)
            if (val) return true
          }
        })
        list = [...new Set(filtered)]
        this.totalUsers = list.length
        this.users = list.length >= 10 ? list.splice(this.startAt, this.tableOptions.itemsPerPage) : list
      }
    },
    resetFilter () {
      this.filter.header = ''
      this.filter.checkbox = []
      this.filter.boxVal = []
    },
    // delete selected user
    async deleteUser (userId) {
      this.$data.$meta.isLoading = true
      const resp = await users.deleteUser(userId)
      if (resp.ok) {
        this.$store.dispatch('showAlert', {
          message: 'User successfully deleted',
          type: 'success',
          callback: this.refreshTable(this.selectedTab)
        })
      } else {
        this.$store.dispatch('showAlert', {
          message: (await resp.json()).error,
          type: 'error'
        })
      }
      this.$data.$meta.isLoading = false
    },
    // reset reason field and validation
    // show modal for activate/deactivate reason field
    reasonDialog (item) {
      this.$v.reason.$reset()
      this.reason = ''
      const modal = this.reasonModal
      modal.header = item.isActive ? 'Enable User' : 'Disable User'
      modal.label = item.isActive ? 'Enable User' : 'Reason for Disabling'
      modal.cancel = () => {
        modal.modal = false
        item.isActive = !item.isActive
        this.reason = 'reason'
      }
      modal.continue = () => {
        this.$v.$touch()
        if (!this.$v.$invalid) {
          this.statusDialog(item, item.isActive ? 'enable' : 'disable')
        }
      }
      modal.modal = true
    },
    // show confirmation dialog for action
    statusDialog (item, action) {
      const buttonColor = action === 'enable' ? 'primary' : ''
      this.$store.dispatch('showDialog', {
        title: `${capitalize(action)} User`,
        message: `Are you sure you want to ${action} ${item.fullName}'s access on DBNMS?`,
        buttonLabel: 'Yes',
        buttonColor: buttonColor,
        callback: () => {
          this.switchUserStatus(item, action)
          this.reasonModal.modal = false
          this.reason = capitalize(action)
          this.$v.reason.$reset()
        }
      })
    },
    // use activate/deactivate user api
    async switchUserStatus (item, action) {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        let resp, label
        if (action === 'enable') {
          label = 'activated'
          resp = await users.activateUser(item._id, { activationReason: this.reason })
        } else {
          label = 'deactivated'
          resp = await users.deactivateUser(item._id, { deactivationReason: this.reason })
        }
        if (resp.ok) {
          this.refreshTable(this.selectedTab)
          this.$store.dispatch('showAlert', {
            message: `User successfully ${label}`,
            type: 'success'
          })
        } else {
          item.isActive = !item.isActive
          this.$store.dispatch('showAlert', {
            message: (await resp.json()).error,
            type: 'error'
          })
        }
      }
    },
    // delete confirmation dialog
    deleteDialog (item) {
      this.$store.dispatch('showDialog', {
        title: 'Delete User',
        message: 'Are you sure you want to delete the selected user? This action cannot be undone.',
        buttonLabel: 'Delete',
        callback: () => { this.deleteUser(item._id) }
      })
    },
    // set registered pic items
    async setRegisteredPic () {
      const query = encodeReqQuery({
        count: 9999999,
        dataview: this.$s.userRole && this.$s.userRole.includes('admin') ? 'default' : 'table',
        advancedQuery: encodeURI(JSON.stringify({ _status: { $ne: 'deleted' }, email: { $ne: this.$s.username }, userRole: { $eq: 'pic' } }))
      })
      const resp = await users.allUsers(query)

      let picList = (await resp.json()).entries
      picList = picList.map((pic) => { return { ...pic, isActive: pic._status === 'active' } })
      let registeredPic = await crud('registration/dpo-list').create({ userDetails: picList.map(({ organization, email }) => { return { organization, email } }) })
      registeredPic = (await registeredPic.json()).validEmails
      this.registeredPic = picList.filter(pic => registeredPic.includes(pic.email))
      this.filteredRegisteredPic = this.registeredPic
      this.unregisteredPicLength = picList.length - registeredPic.length
      this.registeredPicIsLoading = false
    },
    // set PIC filter item
    setPicFilterItems (val) {
      const { value } = this.headers.find(item => item.text === val)
      this.filter.checkbox = this.registeredPic.map(pic => pic[`${value}`])
    },
    // filter Registered Pic
    filterRegisteredPic (val) {
      if (this.filter.header && val.length > 0) {
        const { value } = this.headers.find(item => item.text === this.filter.header)
        this.filteredRegisteredPic = this.registeredPic.filter(pic => val.includes(pic[`${value}`]))
      } else this.filteredRegisteredPic = this.registeredPic
    },
    // call to filter data
    async onFilter (value) {
      await this.filterItems('filter')
      this.tableOptions.page = 1
      if (value.length === 0) this.refreshTable(this.selectedTab)
    }
  },
  validations: {
    reason: custom.reason
  }
}
</script>

<style scoped>

.page {
  /* padding-left: 0; */
  /* box-sizing: border-box; */
  /* height: 100%; */
  display: grid;
  justify-self: center;
  margin: auto;
  max-width:1170px;
}
.container {
  padding:1% 0%;
  /* height: 100%; */
  width: 100%;
}
.shadow{
    box-shadow:4px 8px 8px -2px #c3c3c3 !important;
}
.radius{
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
  }
.v-toolbar >>> .v-toolbar__content{
    padding: 0;
    }
.v-tab--active{
  background-color: rgb(225, 225, 225)
}
.v-tab {
  border-bottom: thin solid rgba(0, 0, 0, 0.12)
}
.status-switch{
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
